import techReact from '../assets/fullstack/tech-react.png';
import techNextjs from '../assets/fullstack/tech-nextjs.png';
import techTypescript from '../assets/fullstack/tech-typescript.png';
import techNodejs from '../assets/fullstack/tech-nodejs.png';
import techPython from '../assets/fullstack/tech-python.png';
import techPostgresql from '../assets/fullstack/tech-postgresql.png';
import techAws from '../assets/fullstack/tech-aws.png';
import techDocker from '../assets/fullstack/tech-docker.png';
import { ICON_NAMES } from '../components/services/editable/commonIcon';
import caseStudyPredictionCover from '../assets/case-study-phase-prediction.jpg';
import caseStudyExecutionCover from '../assets/case-study-phase-execution.jpg';

export { ICON_NAMES };

/**
 * "Service page" template — a generic, CMS-editable version of the
 * Full-Stack & SaaS Development page (originally FullStackDevelopmentPage.jsx).
 * DEFAULT_PAGE_CONTENT below IS that page's original hardcoded copy, so the
 * first servicePages entry (full-stack-development) renders byte-identical
 * to the page that shipped before this refactor.
 */

/** Bundled tech-logo raster assets, keyed for the tech-stack strip editor (no upload widget exists). */
export const TECH_LOGO_MAP = {
  react: techReact,
  nextjs: techNextjs,
  typescript: techTypescript,
  nodejs: techNodejs,
  python: techPython,
  postgresql: techPostgresql,
  aws: techAws,
  docker: techDocker,
};

export const TECH_LOGO_KEYS = Object.keys(TECH_LOGO_MAP);

export const DEFAULT_PAGE_CONTENT = {
  meta: {
    documentTitle: 'Full-Stack & SaaS Development Services | Databiqs',
  },
  /** Section keys hidden from the public page (still shown, dimmed, in the editor). */
  hiddenSections: [],
  /** Card text shown on the /services "Core Capabilities" grid — not rendered on this page itself. */
  cardPreview: {
    icon: 'layers',
    shortDescription: 'A full-stack development company and SaaS development company building MVPs and custom software.',
    features: ['SaaS Development', 'MVP Development', 'Cloud Architecture'],
  },
  hero: {
    kicker: 'Full-stack development company',
    breadcrumbCurrent: 'Full-Stack & SaaS',
    title: 'Full-Stack & SaaS Development Services',
    lead: 'A full-stack development company and SaaS development company. Senior teams build MVPs, SaaS products and custom software with React, Next.js, Node.js, Python and AWS.',
    primaryCtaLabel: 'Book a Free 30-Min Consultation',
    primaryCtaTo: '/book-consultation',
    secondaryCtaLabel: 'See case studies',
    secondaryCtaTo: '/case-studies',
    tags: ['SaaS development', 'MVP development', 'Custom software', 'Hire React developers', 'Dedicated team'],
    glance: {
      statusLabel: 'Taking new projects',
      rows: [
        { icon: 'clock', label: 'MVP timeline', value: '[8–12] weeks' },
        { icon: 'bolt', label: 'MVPs start from', value: 'USD [X]' },
        { icon: 'users', label: 'Engagement', value: 'Fixed price · Dedicated team' },
        { icon: 'shield', label: 'Confidentiality', value: 'NDA on request' },
      ],
      ctaLabel: 'Get a Free Estimate',
    },
  },
  whatWeBuild: {
    kicker: 'What we build',
    title: 'From first MVP to a SaaS product that scales',
    side: 'One senior team covers product design, front end, back end, cloud and QA.',
    cards: [
      { icon: 'layers', title: 'SaaS development', desc: 'Multi-tenant SaaS products with billing, roles, admin panels and analytics built in.' },
      { icon: 'wrench', title: 'MVP development', desc: 'A focused first version you can put in front of users and investors fast.' },
      { icon: 'window', title: 'Custom software development', desc: 'Customer portals, dashboards and internal tools that fit your workflow.' },
      { icon: 'plug', title: 'APIs & integrations', desc: 'Connect payments, CRMs, ERPs and third-party services reliably.' },
      { icon: 'users', title: 'Dedicated development team', desc: 'Hire full-stack developers who work only on your product, billed monthly.' },
      { icon: 'cloud', title: 'Cloud architecture & DevOps', desc: 'Secure, scalable infrastructure on AWS with CI/CD and monitoring.' },
    ],
  },
  whyDatabiqs: {
    kicker: 'Why Databiqs',
    title: 'Why startups choose Databiqs as their MVP development company',
    cards: [
      { icon: 'wrench', title: 'Launch in weeks', desc: 'A focused first version in front of real users, fast.' },
      { icon: 'layers', title: 'One full-stack team', desc: 'Design, front end, back end and cloud, no hand-offs.' },
      { icon: 'users', title: 'Scale the team', desc: "Move from fixed price to a dedicated team when you're ready." },
      { icon: 'code', title: 'Clean code you own', desc: 'Tested, documented code in your own repositories.' },
    ],
  },
  toc: {
    eyebrow: 'On this page',
    items: [
      { id: 'fsd-end-to-end', label: 'Full-stack development services, end to end' },
      { id: 'fsd-timeline-cost', label: 'MVP development: timeline and cost' },
      { id: 'fsd-hire-team', label: 'Hire full-stack developers or a dedicated development team' },
      { id: 'fsd-react-stack', label: 'React, Next.js and Node.js development' },
    ],
    cardTitle: 'Need a quote?',
    cardDesc: 'Free 30-minute call, fixed quote after.',
    cardCtaLabel: 'Book a call',
    cardCtaTo: '/book-consultation',
  },
  longform: [
    {
      id: 'fsd-end-to-end',
      title: 'Full-stack development services, end to end',
      paragraphs: [
        'One team owns the whole product: database, APIs, front end, cloud and testing. That means fewer hand-offs, faster decisions and one point of contact for you.',
        'We work in weekly sprints and show you working software every week, so you can change direction early instead of after launch.',
      ],
    },
    {
      id: 'fsd-timeline-cost',
      title: 'MVP development: timeline and cost',
      paragraphs: [
        'An MVP should be small, not throwaway. We cut scope to the features that prove your idea, but build them on an architecture that can take more users and features later. Most MVPs take [8–12] weeks and cost USD [X]–[Y].',
      ],
    },
    {
      id: 'fsd-hire-team',
      title: 'Hire full-stack developers or a dedicated development team',
      paragraphs: [
        'A fixed-price project suits a clear first version. A dedicated development team suits SaaS products that keep evolving. You hire full-stack developers, React developers or Node.js developers who stay with you month after month.',
      ],
    },
    {
      id: 'fsd-react-stack',
      title: 'React, Next.js and Node.js development',
      paragraphs: [
        'As a React development company and Next.js development agency, we build fast, accessible interfaces. On the back end we use Node.js and Python with PostgreSQL, deployed on AWS with CI/CD, backups and monitoring.',
      ],
    },
  ],
  process: {
    kicker: 'How we work',
    title: 'A clear process, with a demo every week',
    ctaLabel: 'Book a Free 30-Min Consultation',
    ctaTo: '/book-consultation',
    steps: [
      { num: '01', week: 'Week 1', title: 'Discovery', desc: 'We map goals, users, scope and risks, then send a fixed quote and delivery plan.' },
      { num: '02', week: 'Weeks 2–3', title: 'Design', desc: 'Clickable wireframes and UI you review and approve before a line of code.' },
      { num: '03', week: 'Weeks 4–10', title: 'Build', desc: 'Senior engineers ship in weekly sprints, with a live demo every Friday.' },
      { num: '04', week: 'Ongoing', title: 'Launch & support', desc: 'We launch, monitor and hand over code, docs and accounts, all yours.' },
    ],
  },
  techStack: {
    kicker: 'Tech stack',
    title: 'Built on tools that scale',
    items: [
      { name: 'React', logoKey: 'react' },
      { name: 'Next.js', logoKey: 'nextjs' },
      { name: 'TypeScript', logoKey: 'typescript' },
      { name: 'Node.js', logoKey: 'nodejs' },
      { name: 'Python', logoKey: 'python' },
      { name: 'PostgreSQL', logoKey: 'postgresql' },
      { name: 'AWS', logoKey: 'aws' },
      { name: 'Docker', logoKey: 'docker' },
    ],
  },
  caseStudy: {
    kicker: 'Featured case study',
    imageSrc: '',
    imageAlt: 'Product screenshot of a SaaS MVP built by Databiqs full-stack team',
    projectName: '[Project name]',
    meta: 'SaaS MVP · Next.js + Node.js',
    sub: '[Industry] company, [size], named with permission, or "name withheld under NDA".',
    challengeLabel: 'Challenge',
    challengeText: '[The problem the client had, in one or two sentences.]',
    solutionLabel: 'What we built',
    solutionText: '[The solution, stack and timeline.]',
    results: [
      { value: '[Result]', label: 'e.g. first paying users' },
      { value: '[Result]', label: 'e.g. launched in [X] weeks' },
    ],
    linkLabel: 'Read the full case study',
    linkTo: '/case-studies',
  },
  engagement: {
    kicker: 'Engagement models',
    title: 'Pick how you want to work with us',
    noteText: 'Fixed price or dedicated team, you choose',
    tiers: [
      {
        icon: 'file',
        title: 'Fixed-price project',
        desc: 'A defined scope (website, MVP or chatbot) with a fixed quote after a free 30-minute call.',
        bullets: ['Fixed scope and timeline', 'Weekly demos', 'Pay by milestone'],
        highlight: false,
        tag: '',
      },
      {
        icon: 'users',
        title: 'Dedicated development team',
        desc: 'Senior engineers who work only on your product, billed monthly. Scale up or down as you grow.',
        bullets: ['Full-stack, AI or design roles', 'Works in your tools', 'Monthly billing'],
        highlight: true,
        tag: 'Scale with us',
      },
      {
        icon: 'clock',
        title: 'Hourly',
        desc: 'For audits, small changes, integrations and ongoing support after launch.',
        bullets: ['No minimum commitment', 'Transparent time logs', 'Senior engineers only'],
        highlight: false,
        tag: '',
      },
    ],
  },
  faq: {
    kicker: 'FAQ',
    title: 'Questions, answered',
    lead: 'Still have a question? Ask us directly on a free 30-minute call.',
    ctaLabel: 'Book a Free 30-Min Consultation',
    ctaTo: '/book-consultation',
    items: [
      { question: 'How much does MVP development cost?', answer: 'Most MVPs we build cost between USD [X] and USD [Y], depending on features, integrations and platforms. You get a fixed quote after a free 30-minute call.' },
      { question: 'Can I hire full-stack developers or React developers monthly?', answer: 'Yes. Our dedicated-team model lets you bring on full-stack, React or Node.js developers who work only on your product, billed monthly with no long-term lock-in.' },
      { question: 'How fast can you start?', answer: 'Most engagements kick off within [1–2] weeks of the discovery call, once scope and the delivery plan are signed off.' },
      { question: 'Fixed price or dedicated team: which is right for me?', answer: 'Choose fixed price for a clearly scoped first version. Choose a dedicated team if your product will keep evolving after launch and you want the same engineers to stay on.' },
      { question: 'Who owns the code?', answer: 'You do. All code, documentation and infrastructure accounts are handed over and live in your own repositories from day one.' },
      { question: 'Which time zones do you work with?', answer: 'We run weekly demos and daily async updates that overlap with US, UK and EU business hours, whichever suits your team.' },
    ],
  },
  related: {
    kicker: 'Related services',
    title: 'Explore more ways we can help',
    items: [
      { icon: 'globe', title: 'Web Development', desc: 'Custom websites and web apps', to: '/services/web-development' },
      { icon: 'chip', title: 'AI Development', desc: 'Chatbots, agents and automation', to: '/services/ai-automation' },
      { icon: 'calendar', title: 'Free consultation', desc: '30 minutes with a senior engineer', to: '/book-consultation' },
    ],
  },
  blog: {
    kicker: 'From the blog',
    title: 'Guides and answers',
    allLabel: 'All articles',
    allTo: '/blog-page',
    posts: [
      { tag: 'Full-stack', cover: 'MVP timeline and cost', imageSrc: caseStudyPredictionCover, title: 'MVP development: timeline and cost', to: '/blog/mvp-development-timeline-and-cost', readTime: '9 min read', linkLabel: 'Read the guide' },
      { tag: 'Hiring', cover: 'hiring developers', imageSrc: '/service-web.png', title: 'Hire full-stack developers or an agency?', to: '/blog/hire-full-stack-developers-or-an-agency', readTime: '8 min read', linkLabel: 'Read the guide' },
      { tag: 'SaaS', cover: 'SaaS architecture', imageSrc: caseStudyExecutionCover, title: 'SaaS architecture for your first 1,000 customers', to: '/blog/saas-architecture-for-your-first-1000-customers', readTime: '10 min read', linkLabel: 'Read the guide' },
    ],
  },
  finalCta: {
    kicker: 'Start a project',
    title: 'Plan your MVP or SaaS product with us',
    lead: 'Share a few details and a senior engineer will reply within 24 hours.',
    bullets: [
      'Fixed quote after a free 30-minute call',
      'NDA on request',
      'You own 100% of the code',
    ],
    form: {
      badgeText: 'Senior engineer reviews every request',
      title: 'Get a free project estimate',
      sub: 'Tell us a little about your project. We reply within 24 hours.',
      submitLabel: 'Get my free estimate',
      privacyText: 'Your details stay confidential and are never shared or sold. NDA available on request.',
      fields: [
        { key: 'fullName', type: 'text', label: 'Full name', placeholder: 'Jane Smith', required: true },
        { key: 'email', type: 'email', label: 'Work email', placeholder: 'jane@company.com', required: true },
        { key: 'company', type: 'text', label: 'Company name', placeholder: 'Company Inc.', required: false },
        { key: 'phone', type: 'tel', label: 'Phone number', placeholder: '+1 555 000 0000', required: false },
        {
          key: 'services',
          type: 'select',
          label: 'Services',
          required: false,
          options: [
            { value: 'Full-Stack & SaaS Development', label: 'Full-Stack & SaaS Development' },
            { value: 'AI Chatbots', label: 'AI Chatbots' },
            { value: 'AI Automation', label: 'AI Automation' },
            { value: 'Machine Learning', label: 'Machine Learning' },
            { value: 'AI Consulting', label: 'AI Consulting' },
            { value: 'Mobile App Development', label: 'Mobile App Development' },
            { value: 'Digital Marketing', label: 'Digital Marketing' },
            { value: 'Graphic Designing', label: 'Graphic Designing' },
            { value: 'UI/UX Designing', label: 'UI/UX Designing' },
            { value: 'Not sure yet', label: 'Not sure yet' },
          ],
        },
      ],
    },
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

/** Returns a fresh page data tree for a new servicePages entry, with slug/title substituted in. */
export function createServicePageTemplate(slug, title = 'New Service Page') {
  const tpl = deepClone(DEFAULT_PAGE_CONTENT);
  const t = String(title || 'New Service Page');
  tpl.meta.documentTitle = `${t} | Databiqs`;
  tpl.hero.title = t;
  tpl.hero.breadcrumbCurrent = t;
  return tpl;
}

/** Deep-merges stored (possibly partial/older) page data over the current defaults so the renderer never crashes. */
export function normalizeServicePageData(stored, slug = '', title = 'New Service Page') {
  const tpl = createServicePageTemplate(slug, title);
  if (!stored || typeof stored !== 'object') return tpl;

  const mergeArrayOfObjects = (storedArr, tplArr) => {
    if (!Array.isArray(storedArr) || !storedArr.length) return tplArr;
    return storedArr.map((item, i) => ({ ...(tplArr[i] || tplArr[0] || {}), ...(item || {}) }));
  };

  return {
    meta: { ...tpl.meta, ...(stored.meta || {}) },
    hiddenSections: Array.isArray(stored.hiddenSections) ? stored.hiddenSections : tpl.hiddenSections,
    cardPreview: {
      ...tpl.cardPreview,
      ...(stored.cardPreview || {}),
      features: Array.isArray(stored.cardPreview?.features) ? stored.cardPreview.features : tpl.cardPreview.features,
    },
    hero: {
      ...tpl.hero,
      ...(stored.hero || {}),
      tags: Array.isArray(stored.hero?.tags) ? stored.hero.tags : tpl.hero.tags,
      glance: {
        ...tpl.hero.glance,
        ...(stored.hero?.glance || {}),
        rows: mergeArrayOfObjects(stored.hero?.glance?.rows, tpl.hero.glance.rows),
      },
    },
    whatWeBuild: {
      ...tpl.whatWeBuild,
      ...(stored.whatWeBuild || {}),
      cards: mergeArrayOfObjects(stored.whatWeBuild?.cards, tpl.whatWeBuild.cards),
    },
    whyDatabiqs: {
      ...tpl.whyDatabiqs,
      ...(stored.whyDatabiqs || {}),
      cards: mergeArrayOfObjects(stored.whyDatabiqs?.cards, tpl.whyDatabiqs.cards),
    },
    toc: {
      ...tpl.toc,
      ...(stored.toc || {}),
      items: mergeArrayOfObjects(stored.toc?.items, tpl.toc.items),
    },
    longform: mergeArrayOfObjects(stored.longform, tpl.longform),
    process: {
      ...tpl.process,
      ...(stored.process || {}),
      steps: mergeArrayOfObjects(stored.process?.steps, tpl.process.steps),
    },
    techStack: {
      ...tpl.techStack,
      ...(stored.techStack || {}),
      items: mergeArrayOfObjects(stored.techStack?.items, tpl.techStack.items),
    },
    caseStudy: {
      ...tpl.caseStudy,
      ...(stored.caseStudy || {}),
      results: mergeArrayOfObjects(stored.caseStudy?.results, tpl.caseStudy.results),
    },
    engagement: {
      ...tpl.engagement,
      ...(stored.engagement || {}),
      tiers: mergeArrayOfObjects(stored.engagement?.tiers, tpl.engagement.tiers),
    },
    faq: {
      ...tpl.faq,
      ...(stored.faq || {}),
      items: mergeArrayOfObjects(stored.faq?.items, tpl.faq.items),
    },
    related: {
      ...tpl.related,
      ...(stored.related || {}),
      items: mergeArrayOfObjects(stored.related?.items, tpl.related.items),
    },
    blog: {
      ...tpl.blog,
      ...(stored.blog || {}),
      posts: mergeArrayOfObjects(stored.blog?.posts, tpl.blog.posts),
    },
    finalCta: {
      ...tpl.finalCta,
      ...(stored.finalCta || {}),
      bullets: Array.isArray(stored.finalCta?.bullets) ? stored.finalCta.bullets : tpl.finalCta.bullets,
      form: {
        ...tpl.finalCta.form,
        ...(stored.finalCta?.form || {}),
        fields: mergeArrayOfObjects(stored.finalCta?.form?.fields, tpl.finalCta.form.fields),
      },
    },
  };
}

/** Per-block style overrides: { [blockId]: { desktop: {...}, mobile: {...} } }. Empty = inherit page CSS. */
export function createDefaultServicePageStyles() {
  return {};
}

export function normalizeServicePageStyles(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const out = {};
  for (const [blockId, val] of Object.entries(stored)) {
    if (!val || typeof val !== 'object') continue;
    out[blockId] = {
      desktop: val.desktop && typeof val.desktop === 'object' ? { ...val.desktop } : {},
      mobile: val.mobile && typeof val.mobile === 'object' ? { ...val.mobile } : {},
    };
  }
  return out;
}

/** Full servicePages "PageDoc" normalizer used everywhere a stored page is read. */
export function normalizeServicePage(stored, slug, title) {
  const resolvedTitle = title || stored?.title || stored?.data?.hero?.title || 'New Service Page';
  return {
    slug,
    title: resolvedTitle,
    data: normalizeServicePageData(stored?.data, slug, resolvedTitle),
    styles: normalizeServicePageStyles(stored?.styles),
    createdAt: stored?.createdAt || new Date().toISOString(),
    updatedAt: stored?.updatedAt || new Date().toISOString(),
  };
}
